import { parseGraphData, parseNodeData, parseFilterData } from "../utils/utils.jsx";
import logger from "../utils/logger.js";
import axiosInstance from "../security/interceptor";
import { createAnalyticsGraphApi } from "./graphApiAnalytics.js";
import { createGraphApiSupport } from "./graphApiSupport.js";

const graphQLRequest = async (query, options = {}) => {
  const response = await axiosInstance.request({
    method: "post",
    data: JSON.stringify({ query, variables: options?.variables }),
    signal: options?.signal,
  });

  if (response?.data?.errors?.length) {
    throw new Error(response.data.errors[0]?.message || "GraphQL request failed");
  }

  return response?.data?.data;
};

const escapeGraphQLString = (value = "") => String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');

const serializeProperties = (properties) => {
  if (!properties || (typeof properties === "object" && !Object.keys(properties).length)) {
    return "{}";
  }
  if (typeof properties === "string") {
    return escapeGraphQLString(properties);
  }
  return escapeGraphQLString(JSON.stringify(properties));
};

const serializeFilterList = (filters = []) => {
  if (!Array.isArray(filters) || !filters.length) {
    return "[]";
  }

  const normalized = filters
    .map((value) => (typeof value === "string" ? value : String(value ?? "")).trim())
    .filter(Boolean)
    .map((value) => `"${escapeGraphQLString(value)}"`);

  return normalized.length ? `[${normalized.join(", ")}]` : "[]";
};

const ensureMutationSuccess = (result, entity) => {
  if (!result) {
    throw new Error(`No ${entity} data returned.`);
  }
  if (result.error) {
    throw new Error(result.error);
  }
  return result;
};

const runMutation = async ({ query, entity, actionLabel }) => {
  try {
    const data = await graphQLRequest(query);
    return ensureMutationSuccess(data?.[entity], entity);
  } catch (error) {
    const label = actionLabel || `${entity} mutation`;
    logger.error(`${label} failed:`, error);
    throw error;
  }
};

const fetchNodeDetails = async (nodeId) => {
  const query = `query MyQuery {  node(id: "${nodeId}")  {id  label properties}}`;
  const data = await graphQLRequest(query);
  const parsedData = parseNodeData(data);
  if (!parsedData) {
    throw new Error("No Data Availble");
  }
  return parsedData;
};
const fetchNodeConnections = async (nodeID, connectionString) => {
  const query = `query MyQuery {  paths(end: "${connectionString}", start: "${nodeID}"){    edges { id  target   label properties source     }    nodes {      id      label      properties   additionalEdgeCount }  }}`;
  const data = await graphQLRequest(query);

  const parsedData = parseGraphData(data?.paths);
  if (!parsedData || !parsedData?.nodes || !parsedData?.edges) {
    throw new Error("No Data Availble");
  }
  return parsedData;
};

const fetchFlowGraphData = async ({ queryKey }) => {
  const [, search = "", expandLevel = 1, nodeDirection = "", filterCategories = [], expansionFilters = {}] = queryKey;
  const serializedFilters = serializeFilterList(filterCategories);
  const productionLocationFilters = serializeFilterList(expansionFilters?.production_location || []);
  const flowTypeFilters = serializeFilterList(expansionFilters?.flow_type || []);
  const distributorModelFilters = serializeFilterList(expansionFilters?.distributor_model || []);

  const searchArgs = [
    `level:${expandLevel}`,
    `query: "${escapeGraphQLString(search)}"`,
    `filter: ${serializedFilters}`,
    `direction: "${escapeGraphQLString(nodeDirection)}"`,
  ];

  searchArgs.push(`production_location: ${productionLocationFilters}`);
  searchArgs.push(`flow_type: ${flowTypeFilters}`);
  searchArgs.push(`distributor_model: ${distributorModelFilters}`);

  const query = `query MyQuery {  search(${searchArgs.join(",")}) {    edges {      id      label      properties      source      target    }    nodes {      id label  properties additionalEdgeCount additionalEdgeCountEntities   }  }}`;
  const data = await graphQLRequest(query);

  const parsedData = parseGraphData(data?.search, true);

  if (!parsedData || !parsedData?.nodes.length || !parsedData?.edges) {
    throw new Error("No Data Availble");
  }
  return parsedData;
};
const fetchGraphFilterLabels = async ({ type } = {}) => {
  if (type) {
    const safeType = escapeGraphQLString(type);
    const query = `query GraphFilterLabels {
      graphFilter(type: "${safeType}") {
        labels
      }
    }`;
    const data = await graphQLRequest(query);
    return data?.graphFilter?.labels || [];
  }

  const query = `query MyQuery {
    graphFilter {
      distributor_model
      flow_type
      production_location
      labels
      error
    }
  }`;
  const data = await graphQLRequest(query);
  return data?.graphFilter || {};
};

const fetchFilterList = async () => {
  const filterPayload = await fetchGraphFilterLabels();

  const parsedData = parseFilterData(filterPayload);

  if (!parsedData.length) {
    throw new Error("No Filter Data Available");
  }
  return parsedData;
};
const fetchPromptsList = async ({ userId } = {}) => {
  if (!userId) {
    throw new Error("User ID is required for prompts.");
  }
  const safeUserId = escapeGraphQLString(userId);
  const query = `query MyQuery {    graphPrompts(user_id: "${safeUserId}") {  prompts } }`;
  const data = await graphQLRequest(query);

  if (!data?.graphPrompts?.prompts?.length) {
    throw new Error("No prompts Data Available");
  }
  return data?.graphPrompts?.prompts;
};
const fetchSessionSummary = async ({ queryKey }) => {
  const [, userId] = queryKey;

  const query = ` query MyQuery { searchSummary(user_id: "${userId}") { summaryResponse {
      interaction_id
      interaction_timestamp
      question
      session_id
      user_id
      answer
    }
    error }}`;
  const data = await graphQLRequest(query);

  if (!data?.searchSummary?.summaryResponse?.length) {
    throw new Error("No Data Availble");
  }
  return data?.searchSummary?.summaryResponse;
};

const {
  fetchAnalyticsFilters,
  fetchAnalyticsArcData,
  fetchAnalyticsGraphData,
  fetchAnalyticsStatusVehicles,
  fetchAnalyticsCardsData,
  fetchAnalyticsOverview,
  fetchAnalyticsCommonPathData,
  fetchAnalyticsLinearFlowData,
  startVhubAnalyticsAgent,
  startVhubAnalyticsProcessFlowAgent,
} = createAnalyticsGraphApi({ graphQLRequest });

const fetchAutoSuggestions = async (searchTerm, signal, options = {}) => {
  const { includeProperties = false } = options;
  const safeTerm = escapeGraphQLString(searchTerm || "");
  if (!safeTerm.trim()) {
    return { suggestions: [] };
  }

  const propertiesField = includeProperties ? "properties" : "";
  const query = `query MyQuery {  autosuggestion(searchTerm: "${safeTerm}", includeProperties: ${includeProperties}) {    suggestions {      id      label      name      ${propertiesField}    }    error  } }`;
  const data = await graphQLRequest(query, { signal });

  const payload = data?.autosuggestion;
  if (payload?.error) {
    throw new Error(payload.error);
  }

  return { suggestions: payload?.suggestions || [] };
};

const requireRequester = (requestedBy, entityLabel) => {
  if (!requestedBy) {
    throw new Error(`Requestor identity is required to ${entityLabel}.`);
  }
  return escapeGraphQLString(requestedBy);
};

const createNode = async ({ label, properties = {}, requestedBy, comments = "" }) => {
  if (!label) throw new Error("Node label is required.");
  const requester = requireRequester(requestedBy, "create nodes");

  // Build optional comments field for mutation
  const commentsInput = comments ? `, comments: "${escapeGraphQLString(comments)}"` : "";

  const query = `mutation createNode {
    executeNodeOpr(input: {operation: createNode, requested_by: "${requester}", label: "${escapeGraphQLString(label)}", properties: "${serializeProperties(properties)}"${commentsInput}}) {
      id
      success
      operation
      status
      error
      transaction_id
      requested_by
      comments
    }
  }`;
  return runMutation({ query, entity: "executeNodeOpr", actionLabel: "createNode" });
};

const updateNode = async ({ id, properties = {}, label, requestedBy, comments = "" }) => {
  if (!id) throw new Error("Node id is required.");
  const requester = requireRequester(requestedBy, "update nodes");

  const labelInput = label ? `label: "${escapeGraphQLString(label)}",` : "";
  // Build optional comments field for mutation
  const commentsInput = comments ? `, comments: "${escapeGraphQLString(comments)}"` : "";

  const query = `mutation updateNode {
    executeNodeOpr(input: {operation: updateNode, requested_by: "${requester}", ${labelInput} properties: "${serializeProperties(properties)}", id: "${escapeGraphQLString(id)}"${commentsInput}}) {
      success
      operation
      status
      error
      transaction_id
      requested_by
      comments
    }
  }`;

  return runMutation({ query, entity: "executeNodeOpr", actionLabel: "updateNode" });
};

const deleteNode = async ({ id, label, properties = {}, requestedBy, comments = "" }) => {
  if (!id) throw new Error("Node id is required.");
  const requester = requireRequester(requestedBy, "delete nodes");

  const inputFields = ["operation: deleteNode", `requested_by: "${requester}"`, `id: "${escapeGraphQLString(id)}"`];
  if (label) {
    inputFields.push(`label: "${escapeGraphQLString(label)}"`);
  }
  // Include properties (with node name) for proper display in admin views
  inputFields.push(`properties: "${serializeProperties(properties)}"`);
  // Add optional comments
  if (comments) {
    inputFields.push(`comments: "${escapeGraphQLString(comments)}"`);
  }

  const query = `mutation deleteNode {
    executeNodeOpr(input: {${inputFields.join(", ")}}) {
      success
      operation
      status
      error
      transaction_id
      requested_by
      comments
    }
  }`;

  return runMutation({ query, entity: "executeNodeOpr", actionLabel: "deleteNode" });
};

/**
 * Builds enriched properties object with optional node metadata for edge operations.
 * Reduces duplication across createEdge, updateEdge, deleteEdge.
 */
const buildEnrichedEdgeProperties = (
  baseProperties = {},
  { sourceNodeName, targetNodeName, sourceNodeType, targetNodeType, sourceNodeId, targetNodeId } = {},
) => ({
  ...baseProperties,
  ...(sourceNodeName ? { sourceNodeName } : {}),
  ...(targetNodeName ? { targetNodeName } : {}),
  ...(sourceNodeType ? { sourceNodeType } : {}),
  ...(targetNodeType ? { targetNodeType } : {}),
  ...(sourceNodeId ? { sourceNodeId } : {}),
  ...(targetNodeId ? { targetNodeId } : {}),
});

const createEdge = async ({
  sourceNodeId,
  targetNodeId,
  label,
  properties = {},
  requestedBy,
  sourceNodeName,
  targetNodeName,
  sourceNodeType,
  targetNodeType,
  comments = "",
}) => {
  if (!sourceNodeId || !targetNodeId) throw new Error("Edge source and target ids are required.");
  const requester = requireRequester(requestedBy, "create connections");

  const enrichedProperties = buildEnrichedEdgeProperties(properties, {
    sourceNodeName,
    targetNodeName,
    sourceNodeType,
    targetNodeType,
  });

  // Build optional comments field for mutation
  const commentsInput = comments ? `, comments: "${escapeGraphQLString(comments)}"` : "";

  const query = `mutation createEdge {
    executeEdgeOpr(input: {
      sourceNodeId: "${escapeGraphQLString(sourceNodeId)}",
      targetNodeId: "${escapeGraphQLString(targetNodeId)}",
      label: "${escapeGraphQLString(label || "")}",
      operation: createEdge,
      requested_by: "${requester}",
      properties: "${serializeProperties(enrichedProperties)}"${commentsInput}
    }) {
      id
      label
      source
      target
      properties
      success
      operation
      status
      error
      transaction_id
      requested_by
      comments
    }
  }`;

  return runMutation({ query, entity: "executeEdgeOpr", actionLabel: "createEdge" });
};

const updateEdge = async ({
  id,
  sourceNodeId,
  targetNodeId,
  properties = {},
  label,
  requestedBy,
  sourceNodeName,
  targetNodeName,
  sourceNodeType,
  targetNodeType,
  comments = "",
}) => {
  if (!id) throw new Error("Edge id is required.");
  const requester = requireRequester(requestedBy, "update connections");

  const enrichedProperties = buildEnrichedEdgeProperties(properties, {
    sourceNodeName,
    targetNodeName,
    sourceNodeType,
    targetNodeType,
    sourceNodeId,
    targetNodeId,
  });

  const labelInput = label ? `label: "${escapeGraphQLString(label)}",` : "";
  const sourceInput = sourceNodeId ? `sourceNodeId: "${escapeGraphQLString(sourceNodeId)}",` : "";
  const targetInput = targetNodeId ? `targetNodeId: "${escapeGraphQLString(targetNodeId)}",` : "";
  // Build optional comments field for mutation
  const commentsInput = comments ? `, comments: "${escapeGraphQLString(comments)}"` : "";

  const query = `mutation updateEdge {
    executeEdgeOpr(input: {operation: updateEdge, requested_by: "${requester}", id: "${escapeGraphQLString(id)}", ${labelInput}${sourceInput}${targetInput}properties: "${serializeProperties(enrichedProperties)}"${commentsInput}}) {
      id
      label
      source
      target
      properties
      success
      operation
      status
      error
      transaction_id
      requested_by
      comments
    }
  }`;

  return runMutation({ query, entity: "executeEdgeOpr", actionLabel: "updateEdge" });
};

const deleteEdge = async ({
  id,
  label,
  requestedBy,
  sourceNodeName,
  targetNodeName,
  sourceNodeId,
  targetNodeId,
  sourceNodeType,
  targetNodeType,
  comments = "",
}) => {
  if (!id) throw new Error("Edge id is required.");
  const requester = requireRequester(requestedBy, "delete connections");

  const deleteProperties = buildEnrichedEdgeProperties(
    {},
    { sourceNodeName, targetNodeName, sourceNodeType, targetNodeType, sourceNodeId, targetNodeId },
  );

  const inputFields = ["operation: deleteEdge", `requested_by: "${requester}"`, `id: "${escapeGraphQLString(id)}"`];
  if (label) {
    inputFields.push(`label: "${escapeGraphQLString(label)}"`);
  }
  if (Object.keys(deleteProperties).length > 0) {
    inputFields.push(`properties: "${serializeProperties(deleteProperties)}"`);
  }
  // Add optional comments
  if (comments) {
    inputFields.push(`comments: "${escapeGraphQLString(comments)}"`);
  }

  const query = `mutation deleteEdge {
    executeEdgeOpr(input: {${inputFields.join(", ")}}) {
      id
      label
      source
      target
      properties
      success
      operation
      status
      error
      transaction_id
      requested_by
      comments
    }
  }`;

  return runMutation({ query, entity: "executeEdgeOpr", actionLabel: "deleteEdge" });
};

const {
  fetchTransactionRequests,
  fetchConnectionTypes,
  updateTransactionRequest,
  fetchAuditLog,
  fetchListOfConnections,
  submitForApproval,
  updateApprovalRequest,
  fetchApprovalRequests,
  fetchTrackStatusRequests,
  fetchAllProperties,
  fetchGeoMapDataset,
  fetchSecretValue,
} = createGraphApiSupport({
  graphQLRequest,
  escapeGraphQLString,
  serializeProperties,
  runMutation,
  logger,
  fetchGraphFilterLabels,
});

export {
  fetchNodeDetails,
  fetchNodeConnections,
  fetchFlowGraphData,
  fetchFilterList,
  fetchPromptsList,
  fetchAllProperties,
  fetchSessionSummary,
  fetchAnalyticsFilters,
  fetchAnalyticsArcData,
  fetchAnalyticsGraphData,
  fetchAnalyticsStatusVehicles,
  fetchAnalyticsCardsData,
  fetchAnalyticsOverview,
  fetchAnalyticsCommonPathData,
  fetchAnalyticsLinearFlowData,
  startVhubAnalyticsAgent,
  startVhubAnalyticsProcessFlowAgent,
  fetchAutoSuggestions,
  createNode,
  updateNode,
  deleteNode,
  createEdge,
  updateEdge,
  deleteEdge,
  fetchTransactionRequests,
  fetchConnectionTypes,
  updateTransactionRequest,
  fetchAuditLog,
  fetchListOfConnections,
  submitForApproval,
  fetchApprovalRequests,
  fetchTrackStatusRequests,
  updateApprovalRequest,
  fetchGeoMapDataset,
  fetchSecretValue,
};
